import React, { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom'; // ✅ Add this import
import { PieChart } from '@mui/x-charts/PieChart';
import { tryParseJson, formatMetricLabel } from './utils';
import './styles.css';

/**
 * EnergyWidget Component
 * 
 * REFACTORED: Displays energy demand analysis with one pie chart per system
 * Data structure: system -> subsystem -> attributes
 * 
 * - ONE PIE CHART PER SYSTEM (Structural, HVAC, Electrical, etc.)
 * - Different colors for each SUBSYSTEM (Wall, Floor, Roof, etc.)
 * - Segment area = SUM of all ATTRIBUTES in that subsystem
 * - Hover shows attribute breakdown (heating_demand, cooling_demand, etc.)
 */

// Constants
const CHART_COLORS = [
    '#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8',
    '#b1f6cbff', '#422d03ff', '#f9083dff', '#4BC0C0', '#f311efff',
    '#4a0ef1ff', '#c8f518ff', '#a48eecff', '#0f0265ff', '#FF66C3',
    '#9bc7f3ff', '#ccf5b6ff', '#546E7A', '#E91E63', '#0c960cff'
];

const ENERGY_CHART_WIDTH = 400;
const ENERGY_CHART_HEIGHT = 350;

// Shared table cell styles for the component-details table
const thStyle = {
    padding: '8px 10px',
    textAlign: 'left',
    fontWeight: 700,
    fontSize: '11px',
    textTransform: 'uppercase',
    letterSpacing: '0.4px',
    color: '#1565c0',
    borderBottom: '2px solid #90caf9',
    whiteSpace: 'nowrap'
};
const tdStyle = {
    padding: '7px 10px',
    borderBottom: '1px solid #e0e0e0',
    fontSize: '12px',
    color: '#333'
};

// Add this helper function at the top of the file, after the constants
const getSystemIcon = (systemName) => {
    const normalizedName = systemName.toLowerCase().trim();
    
    // Map system names to appropriate emojis
    const iconMap = {
        'structural': '🏗️',
        'structure': '🏗️',
        'electric': '⚡',
        'electrical': '⚡',
        'electricity': '⚡',
        'hvac': '🌡️',
        'heating': '🌡️',
        'ventilation': '🌡️',
        'cooling': '🌡️',
        'plumbing': '🚰',
        'water': '🚰',
        'piping': '🚰',
        'fire safety': '🚒',
        'fire': '🚒',
        'safety': '🚒',
        'sprinkler': '🚒',
        'lighting': '💡',
        'light': '💡',
        'lights': '💡',
        'illumination': '💡'
    };
    
    // Check for exact match first
    if (iconMap[normalizedName]) {
        return iconMap[normalizedName];
    }
    
    // Check for partial match (if system name contains the keyword)
    for (const [key, icon] of Object.entries(iconMap)) {
        if (normalizedName.includes(key)) {
            return icon;
        }
    }
    
    // Default fallback icon
    return '🏢';
};

const EnergyWidget = ({ data, onComplete }) => {
    const [systems, setSystems] = useState([]);
    const [hoveredSubsystem, setHoveredSubsystem] = useState(null);
    const [hoveredSystemIndex, setHoveredSystemIndex] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x:  0, y: 0 });
    const [isLoading, setIsLoading] = useState(true);
    const [energyComponents, setEnergyComponents] = useState({});   // floor -> component -> demand data
    const [expandedFloors, setExpandedFloors] = useState(new Set()); // set of floor systemIds with panel open
    const chartRefs = useRef([]);

    useEffect(() => {
        if (data) {
            if (data.response) processEnergyData(data.response);
            if (data.energyComponents) setEnergyComponents(data.energyComponents);
        }
    }, [data]);

    // ✅ Force colors after render via DOM manipulation AND attach hover events
    useEffect(() => {
        const observers = [];
        const eventListenersStore = [];
        
        systems.forEach((system, systemIndex) => {
            const timer = setTimeout(() => {
                const chartContainer = chartRefs.current[systemIndex];
                if (chartContainer) {
                    // Find all pie arc paths
                    const svg = chartContainer.querySelector('svg');
                    if (svg) {
                        const paths = svg.querySelectorAll('path');
                        
                        // Function to apply actual colors from subsystems
                        const applySubsystemColors = () => {
                            paths.forEach((path, idx) => {
                                // ✅ Map each path index to its corresponding subsystem
                                const subsystem = system.subsystems[idx];
                                
                                if (subsystem && subsystem.color) {
                                    const color = subsystem.color; // Use pre-assigned subsystem color
                                    
                                    // Apply the actual color (not green)
                                    path.setAttribute('fill', color);
                                    path.style.fill = color;
                                    path.style.setProperty('fill', color, 'important');
                                    
                                    // ✅ FIX: Ensure pointer events work for hover
                                    path.style.pointerEvents = 'auto';
                                    path.style.cursor = 'pointer';
                                    
                                    // ✅ FIX: Add data attribute to help with hover detection
                                    path.setAttribute('data-subsystem-id', subsystem.id);
                                    path.setAttribute('data-subsystem-index', idx);
                                } else {
                                    // Fallback to gray if no subsystem match
                                    path.setAttribute('fill', '#CCCCCC'); // Gray fallback
                                    path.style.fill = '#CCCCCC';
                                }
                            });
                        };
                        
                        // Initial color enforcement
                        applySubsystemColors();
                        
                        // ✅ FIX: Attach hover events DIRECTLY to the SVG paths
                        paths.forEach((path, idx) => {
                            const subsystem = system.subsystems[idx];
                            if (subsystem) {
                                // Create event handlers for this path
                                const handleMouseEnter = (e) => {
                                    console.log(`Hovering over: ${subsystem.label}`);
                                    setHoveredSubsystem(subsystem.id);
                                    setHoveredSystemIndex(systemIndex);
                                    
                                    // Update tooltip position
                                    setTooltipPosition({
                                        x: e.clientX + 15,
                                        y: e.clientY + 15
                                    });
                                };
                                
                                const handleMouseMove = (e) => {
                                    // Update tooltip position as mouse moves
                                    setTooltipPosition({
                                        x: e.clientX + 15,
                                        y: e.clientY + 15
                                    });
                                };
                                
                                const handleMouseLeave = () => {
                                    console.log(`Left: ${subsystem.label}`);
                                    setHoveredSubsystem(null);
                                    setHoveredSystemIndex(null);
                                };
                                
                                // Attach event listeners
                                path.addEventListener('mouseenter', handleMouseEnter);
                                path.addEventListener('mousemove', handleMouseMove);
                                path.addEventListener('mouseleave', handleMouseLeave);
                                
                                // Store for cleanup
                                eventListenersStore.push({
                                    path,
                                    mouseenter: handleMouseEnter,
                                    mousemove: handleMouseMove,
                                    mouseleave: handleMouseLeave
                                });
                            }
                        });
                        
                        // MutationObserver to reapply colors when MUI chart resets them during hover/update cycles
                        const observer = new MutationObserver((mutations) => {
                            mutations.forEach((mutation) => {
                                if (mutation.type === 'attributes' && 
                                    (mutation.attributeName === 'fill' || mutation.attributeName === 'style')) {
                                    applySubsystemColors();
                                }
                            });
                        });
                        
                        // Observe all paths for attribute changes
                        paths.forEach(path => {
                            observer.observe(path, {
                                attributes: true,
                                attributeOldValue: true
                            });
                        });
                        
                        observers.push(observer);
                        console.log(`Applied colors and hover events to ${paths.length} slices in ${system.systemName}`);
                    }
                }
            }, 100); // Wait for SVG to render
        });
        
        return () => {
            // Cleanup observers
            observers.forEach(observer => observer.disconnect());
            
            // ✅ CLEANUP: Remove event listeners to prevent memory leaks
            eventListenersStore.forEach(({ path, mouseenter, mousemove, mouseleave }) => {
                path.removeEventListener('mouseenter', mouseenter);
                path.removeEventListener('mousemove', mousemove);
                path.removeEventListener('mouseleave', mouseleave);
            });
        };
    }, [systems]);

    const processEnergyData = (responseData) => {
        try {
            const parsedData = tryParseJson(responseData);
            
            if (!parsedData || typeof parsedData !== 'object') {
                console.warn('[EnergyWidget] Invalid data format');
                setIsLoading(false);
                return;
            }

            const systemsArray = Object.keys(parsedData).map((systemName) => {
                const systemData = parsedData[systemName];
                
                if (!systemData || typeof systemData !== 'object') {
                    return null;
                }

                const subsystemsArray = Object.keys(systemData).map((subsystemName, index) => {
                    const subsystemData = systemData[subsystemName];
                    
                    if (!subsystemData || typeof subsystemData !== 'object') {
                        return null;
                    }

                    // Extract attributes (exclude total_demand and non-numeric fields)
                    const attributes = Object.keys(subsystemData)
                        .filter(key => {
                            // Exclude total_demand and non-numeric metadata fields
                            if (key === 'total_demand' || key === 'annotation' || key === 'name' || key === 'unit') {
                                return false;
                            }
                            // Only include if value is numeric
                            const value = subsystemData[key];
                            return typeof value === 'number' || !isNaN(parseFloat(value));
                        })
                        .map(attrKey => ({
                            name: attrKey,
                            value: subsystemData[attrKey],
                            formattedName: formatMetricLabel(attrKey)
                        }));

                    // Use total_demand if available, otherwise sum attributes
                    const totalValue = subsystemData.total_demand
                        ? parseFloat(subsystemData.total_demand)
                        : attributes.reduce((sum, attr) => {
                            const numValue = parseFloat(attr.value);
                            return !isNaN(numValue) ? sum + numValue : sum;
                        }, 0);

                    return {
                        id: `${systemName}-${subsystemName}`,
                        label: formatMetricLabel(subsystemName),
                        value: totalValue,
                        color: CHART_COLORS[index % CHART_COLORS.length],
                        attributes: attributes,
                        rawName: subsystemName
                    };
                }).filter(Boolean).filter(item => item.value > 0);

                return {
                    systemName: formatMetricLabel(systemName),
                    systemId: systemName,
                    subsystems: subsystemsArray
                };
            }).filter(Boolean).filter(system => system.subsystems.length > 0);

            console.log(`[EnergyWidget] Loaded ${systemsArray.length} systems`);
            setSystems(systemsArray);
            setIsLoading(false);

            if (onComplete) {
                onComplete('Energy widget rendered');
            }
        } catch (err) {
            console.error('[EnergyWidget] Error processing data:', err);
            setIsLoading(false);
        }
    };

    // Handle pie chart hover - detect which segment is hovered
    const handlePieChartMouseMove = (event, systemIndex) => {
        const svgElement = event.currentTarget.querySelector('svg');
        if (!svgElement) return;

        // Get mouse position relative to viewport
        setTooltipPosition({
            x: event.clientX + 15,
            y: event.clientY + 15
        });

        // Find the hovered path element
        const target = event.target;
        if (target.tagName === 'path' || target.tagName === 'PATH') {
            // ✅ TRY 1: Use data attribute if available
            const subsystemIndex = target.getAttribute('data-subsystem-index');
            if (subsystemIndex !== null) {
                const idx = parseInt(subsystemIndex, 10);
                if (idx >= 0 && idx < systems[systemIndex].subsystems.length) {
                    const subsystem = systems[systemIndex].subsystems[idx];
                    setHoveredSubsystem(subsystem.id);
                    setHoveredSystemIndex(systemIndex);
                    return;
                }
            }
            
            // ✅ TRY 2: Fallback to path index method
            const paths = svgElement.querySelectorAll('path');
            const pathIndex = Array.from(paths).indexOf(target);
            
            if (pathIndex >= 0 && pathIndex < systems[systemIndex].subsystems.length) {
                const subsystem = systems[systemIndex].subsystems[pathIndex];
                setHoveredSubsystem(subsystem.id);
                setHoveredSystemIndex(systemIndex);
            }
        }
    };

    const handlePieChartMouseLeave = () => {
        setHoveredSubsystem(null);
        setHoveredSystemIndex(null);
    };

    if (isLoading) {
        return <div className="archie-widget-loading">Loading energy data...</div>;
    }

    if (systems.length === 0) {
        return (
            <div className="kpi-empty-state">
                <p>No energy data available</p>
            </div>
        );
    }

    const title = data.sheetTitle || 'Energy Demand Analysis';
    const description = data.sheetDescription || 'Energy consumption breakdown by system and subsystem';

    return (
        <div 
            className="csv-data-outer energy-widget-container"
            style={{
                backgroundColor: '#f5f5f5',
                color: '#333333',
                padding: '20px',
                borderRadius: '10px'
            }}
        >
            <h2 style={{ color: '#333333' }}>{title}</h2>
            <h4 style={{ color: '#666666' }}>{description}</h4>
            
            <div className="energy-systems-grid">
                {systems.map((system, systemIndex) => {
                    const systemColors = system.subsystems.map(sub => sub.color);
                    
                    return (
                        <div 
                            key={systemIndex} 
                            className="energy-system-card"
                            style={{
                                backgroundColor:  '#ffffff',
                                color: '#333333',
                                border: '1px solid #e0e0e0',
                                borderRadius: '10px',
                                padding: '20px',
                                boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)'
                            }}
                        >
                            <h3 
                                className="energy-system-title"
                                style={{ color: '#333333' }}
                            >
                                <span className="energy-system-icon">{getSystemIcon(system.systemName)}</span>
                                {system.systemName}
                            </h3>
                            
                            <div className="energy-chart-wrapper">
                                {/* Pie Chart - Centered */}
                                <div 
                                    ref={el => chartRefs.current[systemIndex] = el}
                                    className="energy-pie-chart-container"
                                    onMouseMove={(e) => handlePieChartMouseMove(e, systemIndex)}
                                    onMouseLeave={handlePieChartMouseLeave}
                                    style={{ 
                                        backgroundColor: 'white',
                                        padding: '20px',
                                        borderRadius: '8px',
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'center'
                                    }}
                                >
                                    <PieChart
                                        series={[
                                            {
                                                data: system.subsystems.map((sub, idx) => ({
                                                    id: sub.id,
                                                    value: parseFloat(sub.value) || 0,  // ✅ Always numeric
                                                    // ✅ Remove label completely
                                                    // label: sub.label,  // COMMENT OUT
                                                    // ✅ Use actual subsystem color
                                                    color: sub.color,  // Use actual color from CHART_COLORS
                                                })),
                                                highlightScope:  { faded: 'global', highlighted: 'item' },
                                                innerRadius: 50,
                                                outerRadius: 120,
                                                paddingAngle: 2,
                                                cornerRadius: 4,
                                                // ✅ Multiple ways to disable arc labels
                                                arcLabel: () => null,
                                                arcLabelMinAngle: 360,
                                                valueFormatter: () => '',  // ✅ Return empty string for all values
                                            },
                                        ]}
                                        colors={system.subsystems.map(sub => sub.color)}  // ✅ Use actual subsystem colors
                                        width={ENERGY_CHART_WIDTH}
                                        height={ENERGY_CHART_HEIGHT}
                                        margin={{ top:  20, bottom: 20, left: 20, right: 20 }}
                                        slotProps={{
                                            legend: {
                                                hidden: true
                                            },
                                            pieArcLabel: {  // ✅ ADD THIS
                                                hidden: true,
                                            },
                                            tooltip: {  // ✅ ADD THIS - hide tooltips too
                                                hidden: true,
                                            },
                                        }}
                                        sx={{
                                            // Remove hard-coded fill colors - let useEffect handle colors
                                            '&&&&& path': {
                                                // Keep high specificity for white borders
                                                stroke: '#ffffff',
                                                strokeWidth: 3,
                                            },
                                            '& .MuiChartsLegend-root': {
                                                display: 'none !important'
                                            },
                                            // Text removal (keep existing)
                                            '& text': {
                                                display: 'none !important',
                                            },
                                            '& .MuiChartsAxis-label': {
                                                display: 'none !important',
                                            },
                                            '& .MuiChartsAxis-tickLabel': {
                                                display: 'none !important',
                                            },
                                            '& tspan': {
                                                display: 'none !important',
                                            },
                                            '& .MuiPieArc-root': {
                                                stroke: '#ffffff',
                                                strokeWidth: 3,
                                                cursor: 'pointer',
                                            },
                                            '& .MuiPieArc-root:hover': {
                                                filter: 'brightness(1.15)',
                                                strokeWidth: 4,
                                            },
                                        }}
                                    />
                                </div>
                                
                                {/* Legend - Below pie chart */}
                                <div className="energy-legend-container">
                                    {system.subsystems.map((subsystem) => {
                                        const totalSystemValue = system.subsystems.reduce((sum, s) => sum + s.value, 0);
                                        const percentage = ((subsystem.value / totalSystemValue) * 100).toFixed(1);
                                        
                                        return (
                                            <div
                                                key={subsystem.id}
                                                className="energy-legend-item"
                                                style={{
                                                    display: 'flex',
                                                    alignItems: 'left',
                                                    gap:  '10px',
                                                    padding: '8px 12px',
                                                    backgroundColor: '#f9f9f9',
                                                    borderRadius: '6px',
                                                    color: '#333333'
                                                }}
                                            >
                                                <span 
                                                    className="energy-legend-color" 
                                                    style={{ 
                                                        backgroundColor: subsystem.color,
                                                        width: '16px',
                                                        height: '16px',
                                                        borderRadius: '3px',
                                                        flexShrink:  0,
                                                        border: '1px solid rgba(0, 0, 0, 0.1)'
                                                    }}
                                                />
                                                <span 
                                                    className="energy-legend-label"
                                                    style={{
                                                        flex: 1,
                                                        fontSize: '14px',
                                                        fontWeight: 500,
                                                        color:  '#333333'
                                                    }}
                                                >
                                                    {subsystem. label}
                                                </span>
                                                <span 
                                                    className="energy-legend-value"
                                                    style={{
                                                        fontSize: '13px',
                                                        color: '#666666',
                                                        fontWeight: 600
                                                    }}
                                                >
                                                    {subsystem.value.toFixed(2)} ({percentage}%)
                                                </span>
                                            </div>
                                        );
                                    })}
                                </div>

                                {/* ── Component Details Toggle Button ── */}
                                {(() => {
                                    // The floor name in energyComponents uses the raw systemId key
                                    const floorComponents = energyComponents[system.systemId];
                                    if (!floorComponents || Object.keys(floorComponents).length === 0) return null;
                                    const isExpanded = expandedFloors.has(system.systemId);
                                    const componentEntries = Object.entries(floorComponents);
                                    const demandCols = ['heating_demand', 'cooling_demand', 'lighting_demand', 'equipment_demand'];
                                    return (
                                        <div style={{ marginTop: '16px' }}>
                                            <button
                                                onClick={() => {
                                                    setExpandedFloors(prev => {
                                                        const next = new Set(prev);
                                                        if (next.has(system.systemId)) next.delete(system.systemId);
                                                        else next.add(system.systemId);
                                                        return next;
                                                    });
                                                }}
                                                style={{
                                                    width: '100%',
                                                    padding: '10px 16px',
                                                    background: isExpanded ? '#1565c0' : '#1976d2',
                                                    color: '#fff',
                                                    border: 'none',
                                                    borderRadius: '6px',
                                                    cursor: 'pointer',
                                                    fontSize: '13px',
                                                    fontWeight: 600,
                                                    letterSpacing: '0.3px',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    gap: '8px'
                                                }}
                                            >
                                                {isExpanded ? '▲' : '▼'}&nbsp;
                                                {isExpanded ? 'Hide' : 'Show'} Component Details ({componentEntries.length})
                                            </button>

                                            {isExpanded && (
                                                <div style={{ marginTop: '12px', overflowX: 'auto' }}>
                                                    <table style={{
                                                        width: '100%',
                                                        borderCollapse: 'collapse',
                                                        fontSize: '12px',
                                                        color: '#333'
                                                    }}>
                                                        <thead>
                                                            <tr style={{ background: '#e3f2fd' }}>
                                                                <th style={thStyle}>Component</th>
                                                                <th style={thStyle}>Heating (kWh)</th>
                                                                <th style={thStyle}>Cooling (kWh)</th>
                                                                <th style={thStyle}>Lighting (kWh)</th>
                                                                <th style={thStyle}>Equipment (kWh)</th>
                                                                <th style={thStyle}>Total (kWh)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {componentEntries.map(([compName, compData], ci) => (
                                                                <tr key={ci} style={{ background: ci % 2 === 0 ? '#fafafa' : '#fff' }}>
                                                                    <td style={tdStyle}>{compName}</td>
                                                                    {demandCols.map(col => (
                                                                        <td key={col} style={{ ...tdStyle, textAlign: 'right' }}>
                                                                            {(compData[col] || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                        </td>
                                                                    ))}
                                                                    <td style={{ ...tdStyle, textAlign: 'right', fontWeight: 700, color: '#1565c0' }}>
                                                                        {(compData.total_demand || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                                                    </td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            )}
                                        </div>
                                    );
                                })()}
                            </div>
                        </div>
                    );
                })}
            </div>

            {/* Floating Tooltip - Rendered outside cards with portal-like positioning */}
            {hoveredSubsystem && hoveredSystemIndex !== null && createPortal(
                (() => {
                    const system = systems[hoveredSystemIndex];
                    const hoveredData = system.subsystems.find(s => s.id === hoveredSubsystem);
                    
                    if (!hoveredData) return null;

                    console.log('Rendering tooltip for:', hoveredData.label);

                    const hasAttributes = hoveredData.attributes && hoveredData.attributes.length > 0;
                    const totalSystemValue = system.subsystems.reduce((sum, s) => sum + s.value, 0);
                    const percentage = ((hoveredData.value / totalSystemValue) * 100).toFixed(1);

                    return (
                        <div
                            className="energy-tooltip-portal"
                            style={{
                                position: 'fixed',
                                left: `${tooltipPosition.x}px`,
                                top: `${tooltipPosition.y}px`,
                                zIndex: 999999,
                                pointerEvents: 'none',
                                backgroundColor: '#ffffff',
                                border: '2px solid #2196F3',
                                borderRadius: '8px',
                                padding: '16px',
                                boxShadow: '0 8px 24px rgba(0, 0, 0, 0.2)',
                                minWidth: '220px',
                                maxWidth: '320px',
                                color: '#333333'
                            }}
                        >
                            <div 
                                style={{
                                    fontSize: '15px',
                                    fontWeight: 700,
                                    color: '#333333',
                                    marginBottom: '10px',
                                    paddingBottom: '8px',
                                    borderBottom: '2px solid #2196F3'
                                }}
                            >
                                {hoveredData.label}
                            </div>
                            <div>
                                <div 
                                    style={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '12px',
                                        fontWeight: 'bold', 
                                        borderBottom: '1px solid #e0e0e0',
                                        paddingBottom: '8px',
                                        marginBottom: '10px',
                                        color: '#333333',
                                        fontSize: '14px'
                                    }}
                                >
                                    <span>Total: </span>
                                    <span style={{ color: '#2196F3' }}>{hoveredData.value.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} kWh</span>
                                </div>
                                <div 
                                    style={{ 
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        gap: '12px',
                                        fontSize: '13px',
                                        color: '#666666',
                                        marginBottom: '10px',
                                        paddingBottom: '8px',
                                        borderBottom: '1px solid #e0e0e0'
                                    }}
                                >
                                    <span>Share:</span>
                                    <span style={{ color: '#2196F3', fontWeight: 600 }}>{percentage}%</span>
                                </div>
                                {hasAttributes && (
                                <>
                                <div style={{
                                    fontSize: '11px',
                                    color: '#666',
                                    marginBottom: '8px',
                                    fontWeight: 'bold',
                                    textTransform: 'uppercase',
                                    letterSpacing: '0.5px'
                                }}>
                                    Attributes
                                </div>
                                {hoveredData.attributes.map((attr, attrIndex) => (
                                    <div 
                                        key={attrIndex}
                                        style={{
                                            display: 'flex',
                                            justifyContent: 'space-between',
                                            gap: '12px',
                                            fontSize: '13px',
                                            color: '#333333',
                                            marginBottom: '6px',
                                            padding: '4px 0'
                                        }}
                                    >
                                        <span 
                                            style={{
                                                color: '#666666',
                                                fontWeight: 500
                                            }}
                                        >
                                            {attr.formattedName}: 
                                        </span>
                                        <span 
                                            style={{
                                                color: '#2196F3',
                                                fontWeight: 700
                                            }}
                                        >
                                            {typeof attr.value === 'number' 
                                                ? attr.value.toFixed(2) 
                                                : attr.value}
                                        </span>
                                    </div>
                                ))}
                                </>
                                )}
                            </div>
                        </div>
                    );
                })(),
                document.body // ✅ Render directly to document.body
            )}
        </div>
    );
};

export default EnergyWidget;
